import { useMemo, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";

import { passwordChanged } from "../../../redux/slices/loginSlice";
import { resetPasswordAuthorized } from "../../../redux/slices/projectsSlice";
import PasswordField from "../../../components/PasswordField";

// Shown when the password that just worked was one an admin set: it is a way in,
// not a password of their own, so the app asks for a real one before anything
// else and will not let this be dismissed. The admin who set it knows it.
const SetOwnPasswordDialog = () => {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const user = useAppSelector((state) => state.login.user) as any;
  const resetting = useAppSelector(
    (state: any) => state.profile?.passwordResetting,
  );

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const open = !!user?.mustChangePassword;

  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const mismatched = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit = useMemo(
    () => newPassword.length >= 8 && newPassword === confirmPassword,
    [newPassword, confirmPassword],
  );

  const handleSubmit = async () => {
    setError(null);
    if (!canSubmit) {
      setError("Choose a password of at least 8 characters, twice.");
      return;
    }
    const result: any = await dispatch(
      resetPasswordAuthorized({ newPassword }),
    );
    if (result.error) {
      setError(result.payload || "Could not set the password.");
      return;
    }
    // The requirement is settled for this session and the stored user, so the
    // dialog does not come back until an admin sets another temporary password.
    dispatch(passwordChanged());
    enqueueSnackbar("Your password has been set", { variant: "success" });
  };

  if (!open) return null;

  return (
    <Dialog
      open
      // Not dismissible: the point is that this password stops being the way in.
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Choose a password</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          An administrator gave you a temporary password to get in. Please choose
          one of your own now — it is the last step, and the temporary one stops
          working.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <PasswordField
            autoFocus
            fullWidth
            size="small"
            label="New password"
            value={newPassword}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewPassword(event.target.value)}
            error={tooShort}
            helperText={tooShort ? "At least 8 characters." : " "}
          />
          <PasswordField
            fullWidth
            size="small"
            label="Confirm new password"
            value={confirmPassword}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(event.target.value)}
            error={mismatched}
            helperText={mismatched ? "The two passwords do not match." : " "}
          />
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={resetting || !canSubmit}
          startIcon={resetting ? <CircularProgress size={20} /> : undefined}
        >
          Set password
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetOwnPasswordDialog;
